// @flow
/** @jsx jsx */
import React from 'react';
import { partial } from 'lodash';
import { css, jsx } from '@emotion/core';

import {
  type ApiKeys,
  type PaymentSource,
  type PaymentSourceMethod,
  type SupportedPaymentMethod,
  paymentSourceMethods,
} from '@zapier/payment/src/types';
import { Button, ButtonGroup, Link, Text } from '@zapier/design-system';
import PaymentMethodImage from '@zapier/payment/src/components/PaymentMethodImage';
import PaypalButton from '@zapier/payment/src/components/PaypalButton';
import PaypalInfo from '@zapier/payment/src/components/PaypalInfo';
import { useSize } from '@zapier/payment/src/hooks/useSize';
import PaymentMethod from '../PaymentMethod';
import PaymentMethodSelect from '../PaymentMethodSelect';

type Props = {
  /** Payment processor API keys */
  apiKeys: ApiKeys,
  /** Currently select method */
  currentMethod?: PaymentSourceMethod,
  /** Hide switch component button */
  hideSwitch: boolean,
  /** Show editor */
  isEditing: boolean,
  /** Call to canel edit */
  onCancelEdit: () => void,
  /** Call to start edit */
  onEdit: () => void,
  /** Call when editor throws an error */
  onErrorEdit: Error => void,
  /** Call to save current edit */
  onSaveEdit: () => void,
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceMethod) => void,
  /** User's currently saved paypal details */
  savedSource: ?PaymentSource,
  /** Supported payment methods (bank, card etc.) */
  supportedMethods: Array<SupportedPaymentMethod>,
};

const buttonWrapCss = css`
  margin: 15px;
`;

const flexCss = css`
  text-align: center;
`;

const renderLabel = (props: Props) =>
  props.savedSource && !props.isEditing ? (
    <PaypalInfo paypal={props?.savedSource?.details} />
  ) : (
    <React.Fragment>
      <PaymentMethodImage
        imageType={paymentSourceMethods.paypal}
        isSmall={true}
      />
      <Text tag="h3" type="subtitle700">
        Connect PayPal
      </Text>
    </React.Fragment>
  );

const renderAside = (props: Props, isSmall: boolean) => {
  if (!props.isEditing) {
    return (
      <Button
        color="greyAlt"
        label="Edit Payment Method"
        onClick={props.onEdit}
      >
        {props.savedSource ? `Edit${!isSmall ? ' Payment Method' : ''}` : 'Add'}
      </Button>
    );
  } else {
    return props.hideSwitch ? null : (
      <PaymentMethodSelect
        currentMethod={props.currentMethod}
        onChange={props.onSelectSource}
        supportedMethods={props.supportedMethods}
      />
    );
  }
};

const PaymentMethodPaypal = (props: Props) => {
  const size = useSize();
  const isSmall = size === 'small';

  return (
    <PaymentMethod
      isEditing={props.isEditing}
      renderAside={partial(renderAside, props, isSmall)}
      renderLabel={partial(renderLabel, props)}
    >
      <div css={flexCss}>
        <Text type="bodyText800">
          {props.savedSource
            ? 'You can reconnect to use a different PayPal account.'
            : 'Please connect your PayPal account.'}
        </Text>
        <ButtonGroup
          columnSize={isSmall ? '100%' : 'minmax(100px, 250px)'}
          justify="center"
        >
          <div css={buttonWrapCss}>
            <Button
              color="greyAlt"
              isFullWidth={true}
              label="Cancel"
              onClick={props.onCancelEdit}
            >
              Cancel
            </Button>
          </div>
          <PaypalButton
            apiKey={props.apiKeys.braintree}
            margin={isSmall ? 'bottom' : 'left'}
            onAuthorize={props.onSaveEdit}
            onError={props.onErrorEdit}
          />
        </ButtonGroup>
        <Link
          href="https://www.paypal.com/cgi-bin/webscr?cmd=xpt/Marketing/popup/OLCWhatIsPayPal-outside"
          target="_blank"
        >
          What is PayPal?
        </Link>
      </div>
    </PaymentMethod>
  );
};

export default PaymentMethodPaypal;
